import getDataFromApi from "../../components/getDataFromApi";
import { getCokkie } from "../login/Login";
import {
  canUseBootstrapCredentials,
  extractTerminalCredentials,
  hasUsableTerminalCredentials,
} from "./DeviceCredentialsForm";

/**
 * Terminal backend access.
 *
 * Same-origin (recommended): TERMINAL_URL empty / unset → UI at /develop-terminal,
 * APIs at /api/* and WS at /terminal (proxied in dev; nginx in prod).
 *
 * Legacy: TERMINAL_URL = "http://localhost:3001" (direct two-port mode).
 */

function getTerminalConfig() {
  const raw = window.ENV?.TERMINAL_URL;
  const isSameOrigin =
    raw === undefined ||
    raw === null ||
    raw === "" ||
    raw === "/" ||
    raw === ".";

  if (isSameOrigin) {
    return {
      apiBase: "",
      embedBase: "/develop-terminal",
    };
  }

  const base = String(raw).replace(/\/$/, "");
  return {
    apiBase: base,
    embedBase: base,
  };
}

export function buildTerminalEmbedUrl(device = {}, credentials = {}) {
  const { embedBase } = getTerminalConfig();
  const params = new URLSearchParams();
  const deviceId =
    credentials?.deviceId ||
    device?.client_id ||
    device?.device_code ||
    device?.stream_id ||
    "";
  const deviceLanIp =
    credentials?.deviceLanIp ||
    (device?.device_ip && device.device_ip !== "--" ? device.device_ip : "");
  const deviceUser =
    credentials?.deviceUser || device?.ssh_user || device?.device_user || "";

  const chipset =
    (credentials?.chipset && credentials.chipset !== "--" ? credentials.chipset : "") ||
    (device?.chipset && device.chipset !== "--" ? device.chipset : "");
  const family =
    (credentials?.family && credentials.family !== "--" ? credentials.family : "") ||
    (device?.family && device.family !== "--" ? device.family : "");
  const displayName = credentials?.displayName || device?.name || "";

  if (deviceId) params.set("deviceId", deviceId);
  if (deviceLanIp) params.set("deviceLanIp", deviceLanIp);
  if (deviceUser) params.set("deviceUser", deviceUser);
  if (displayName) params.set("displayName", displayName);
  if (chipset) params.set("chipset", chipset);
  if (family) params.set("family", family);
  params.set("autoConnect", "1");
  params.set("embed", "1");
  return `${embedBase}/?${params.toString()}`;
}

function decodeJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return {};
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) || {};
  } catch {
    return {};
  }
}

export function getLoggedInUserEmail() {
  const token = getCokkie("jwtToken");
  const payload = token ? decodeJwtPayload(token) : {};
  const storedName = String(localStorage.getItem("name") || "").trim();
  const storedEmail = String(localStorage.getItem("email") || "").trim();
  const candidates = [
    payload.email,
    payload.useremail,
    payload.user_email,
    storedEmail,
    payload.username,
    payload.preferred_username,
    storedName,
    payload.sub,
  ];
  const withAt = candidates.find((value) => String(value || "").includes("@"));
  if (withAt) return String(withAt).trim();
  return String(candidates.find(Boolean) || "").trim();
}

export async function fetchDeviceCredentials({ userEmail, deviceId } = {}) {
  const email = userEmail || getLoggedInUserEmail();
  if (!email) {
    throw new Error("Logged-in user email is required to load device credentials.");
  }
  if (!deviceId) {
    throw new Error("Device id is required to load device credentials.");
  }
  const params = new URLSearchParams({
    useremail: email,
    device_id: deviceId,
  });
  return getDataFromApi(
    `${window.ENV.API_URL}/forms/devices/get_device_credentials?${params.toString()}`,
    "get"
  );
}

export async function fetchAndConnectTerminal(device = {}, extras = {}) {
  const recordId = extras.recordId || device._id;
  if (!recordId) {
    throw new Error("Device id is required to load credentials.");
  }

  const credentialResponse = await fetchDeviceCredentials({
    deviceId: recordId,
  });
  const apiCredentials = extractTerminalCredentials(credentialResponse, {}, {});
  const savedCredentials = extractTerminalCredentials({}, {}, device);
  const credentials = {
    ...savedCredentials,
    ...apiCredentials,
    deviceId:
      extras.deviceId ||
      device.client_id ||
      device.device_code ||
      savedCredentials.deviceId,
    deviceLanIp:
      extras.deviceLanIp ||
      apiCredentials.deviceLanIp ||
      savedCredentials.deviceLanIp ||
      (device.device_ip && device.device_ip !== "--" ? device.device_ip : ""),
    deviceUser: apiCredentials.deviceUser || savedCredentials.deviceUser,
    bootstrapUser: apiCredentials.bootstrapUser,
    bootstrapPassword: apiCredentials.bootstrapPassword,
    platformUserId: apiCredentials.platformUserId,
    recordId: extras.recordId || device._id,
    displayName: extras.displayName || device.name || "",
    chipset:
      extras.chipset ||
      (device.chipset && device.chipset !== "--" ? device.chipset : ""),
    family:
      extras.family ||
      (device.family && device.family !== "--" ? device.family : ""),
  };

  if (!hasUsableTerminalCredentials(credentials)) {
    throw new Error("Device credentials are incomplete. Cannot open the terminal.");
  }

  const connectResult = await connectTerminalSession(credentials);
  return { credentials, device, connectResult };
}

export async function connectTerminalSession(credentials = {}) {
  const { apiBase } = getTerminalConfig();
  if (!credentials?.deviceId) {
    throw new Error("Device id is required to open the terminal.");
  }
  const hasBootstrap = canUseBootstrapCredentials(credentials);
  if (!hasBootstrap && !credentials?.deviceUser) {
    throw new Error("Device username is required to open the terminal.");
  }

  const response = await fetch(`${apiBase}/api/connect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      deviceId: credentials.deviceId,
      deviceLanIp: credentials.deviceLanIp,
      // With bootstrap, server creates this user (from user_id) then SSHs in via key.
      deviceUser: credentials.deviceUser || undefined,
      sshPassword: hasBootstrap ? undefined : credentials.sshPassword || undefined,
      sshKeyPath: credentials.sshKeyPath || undefined,
      bootstrapUser: credentials.bootstrapUser || undefined,
      // Explicit null/empty is OK for root key-auth bootstrap (do not drop the field).
      bootstrapPassword: hasBootstrap
        ? credentials.bootstrapPassword || ""
        : undefined,
      recordId: credentials.recordId || undefined,
      displayName: credentials.displayName || undefined,
      chipset: credentials.chipset || undefined,
      family: credentials.family || undefined,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || (!data.success && !data.message)) {
    throw new Error(data.error || `Terminal connect failed (${response.status})`);
  }
  return data;
}

export async function disconnectTerminalSession(deviceId) {
  const { apiBase } = getTerminalConfig();
  if (!deviceId) return null;
  const response = await fetch(`${apiBase}/api/disconnect`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceId }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Terminal disconnect failed (${response.status})`);
  }
  return data;
}

function extractFarmItems(source) {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];
  const nested =
    source.items ||
    source.results ||
    source.device_farms ||
    source.device_farm ||
    source.data;
  if (Array.isArray(nested)) return nested;
  return [];
}

function extractChipsetDevices(source) {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];
  const payload =
    source.chipset_device_info ||
    source.chipset_devices ||
    source.devices ||
    source.data ||
    source;
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    return Object.values(payload).flatMap((section) => {
      if (Array.isArray(section)) return section;
      if (Array.isArray(section?.devices)) return section.devices;
      return [];
    });
  }
  return [];
}

async function readFirstSsePayload(url) {
  const token = getCokkie("jwtToken");
  if (!token) return null;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(url, {
      headers: {
        accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    });
    if (!response.ok || !response.body) return null;
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const splitAt = buffer.indexOf("\n\n");
      if (splitAt === -1) continue;
      const block = buffer.slice(0, splitAt);
      const dataLine = block
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim())
        .join("");
      await reader.cancel();
      if (!dataLine) return null;
      return JSON.parse(dataLine);
    }
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
  return null;
}

let chipsetCatalogPromise = null;

async function loadChipsetCatalog() {
  if (chipsetCatalogPromise) return chipsetCatalogPromise;
  chipsetCatalogPromise = (async () => {
    const catalog = {};
    const farmResponse = await getDataFromApi(
      `${window.ENV.API_URL}/devices/device_farm?page=1&page_size=50`,
      "get"
    );
    const farms = extractFarmItems(farmResponse);
    await Promise.all(
      farms.map(async (farm) => {
        const farmId = farm?._id || farm?.id;
        if (!farmId) return;
        const params = new URLSearchParams();
        params.set("filters", JSON.stringify({ device_farm_id: farmId }));
        const payload = await readFirstSsePayload(
          `${window.ENV.API_URL}/devices?${params.toString()}`
        );
        extractChipsetDevices(payload).forEach((item) => {
          const key = String(
            item?.client_id || item?.device_code || item?.stream_id || ""
          ).trim();
          const chipset = String(item?.chipset || "").trim();
          if (!key || !chipset || chipset === "--") return;
          catalog[key] = {
            chipset,
            family: item?.family && item.family !== "--" ? item.family : "",
            name: item?.name || "",
            recordId: item?._id || "",
          };
        });
      })
    );
    return catalog;
  })();
  try {
    return await chipsetCatalogPromise;
  } catch (err) {
    chipsetCatalogPromise = null;
    throw err;
  }
}

export async function enrichTerminalDeviceChipsets() {
  const { apiBase } = getTerminalConfig();
  const listed = await fetch(`${apiBase}/api/devices`)
    .then((r) => r.json())
    .catch(() => []);
  const devices = Array.isArray(listed) ? listed : [];
  const missing = devices.filter((item) => !String(item?.chipset || "").trim());
  if (!missing.length) return devices;

  const catalog = await loadChipsetCatalog();
  await Promise.all(
    missing.map(async (item) => {
      const info = catalog[item.id];
      if (!info) return;
      await fetch(`${apiBase}/api/device-meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deviceId: item.id,
          displayName: info.name || undefined,
          chipset: info.chipset,
          family: info.family || undefined,
          recordId: info.recordId || undefined,
        }),
      }).catch(() => {});
    })
  );
  return fetch(`${apiBase}/api/devices`)
    .then((r) => r.json())
    .catch(() => devices);
}
